/* eslint-disable react/prop-types, react/no-unused-prop-types */
import type { AriaTextFieldProps } from '@react-types/textfield';
import type { InputHTMLAttributes } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTextField } from 'react-aria';

import type { VariantProp } from '../../common/colors';
import type { RenameKeys } from '../../types/rename_keys';
import type { ControlSize } from '../shared/types';
import { Icon } from '../../assets/Icon/Icon';
import { shadows } from '../../common/shadows';
import {
  getVariantStyles,
  variantAccentColor,
  variantBodyColor,
  variantIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { stateZIndexes } from '../../common/z_indexes';
import { type InputSharedProps } from '../../components/BaseInput/BaseInput';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { Button } from '../Button/Button';
import { ControlProvider, useControlSize } from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import {
  controlBorderRadius,
  controlGap,
  controlIconSize,
  controlPaddingX,
  controlPaddingY,
  controlTextDecoration,
  controlTextDecorationValue,
  controlVars,
  getControlSubcontrolSize,
} from '../shared/vars';

const InlineInputAffixContent = styled(Text, {
  display: 'flex',
  whiteSpace: 'nowrap',
  widthAll: 'min-content',
  color: variantBodyColor,
});
InlineInputAffixContent.displayName = 'InlineInputAffixContent';

const InlineInputClear = styled(Button, {
  display: 'none',
});
InlineInputClear.displayName = 'InlineInputClear';

const InlineInputAffix = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: controlGap,
  width: 'min-content',
});
InlineInputAffix.displayName = 'InlineInputAffix';

const InlineInputIcon = styled(Icon, {
  size: controlIconSize,
  color: variantIconColor,
});
InlineInputIcon.displayName = 'InlineInputIcon';

const InlineInputField = styled('input', {
  zIndex: stateZIndexes.initial,
  display: 'flex',
  width: '100%',
  minWidth: 0,
  background: colors.transparent,
  color: variantBodyColor,
  textDecoration: controlTextDecoration,

  [selectors.placeholder]: {
    color: variantAccentColor,
  },

  [selectors.focus]: {
    outline: 'none',
  },
});
InlineInputField.displayName = 'InlineInputField';

const InlineInputElements = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: controlGap,
  width: '100%',
  minWidth: 0,
});
InlineInputElements.displayName = 'InlineInputElements';

const InlineInputBoundary = styled('div', {
  position: 'absolute',
  zIndex: stateZIndexes.boundary,
  top: `calc(${controlPaddingY} * -1)`,
  right: `calc(${controlPaddingX} * -1)`,
  bottom: `calc(${controlPaddingY} * -1)`,
  left: `calc(${controlPaddingX} * -1)`,
  background: '$$boundaryBackground',
  strokeAll: variantStrokeColor,
  borderRadius: controlBorderRadius,
  opacity: '$$boundaryOpacity',
  transition: transitions.control,
});
InlineInputBoundary.displayName = 'InlineInputBoundary';

const InlineInputContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  maxWidth: '100%',
  transition: transitions.control,
  cursor: 'pointer',
  $$boundaryBackground: colors.bgApplicationLight,
  $$boundaryOpacity: 0,
  [controlTextDecoration]: controlTextDecorationValue,

  [darkThemeSelector]: {
    $$boundaryBackground: colors.bgApplicationDark,
  },

  [selectors.focus]: {
    outline: 'none',

    [`& ${InlineInputBoundary}`]: {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  [selectors.interactive]: {
    $$boundaryOpacity: 1,

    [`& ${InlineInputClear}`]: {
      display: 'flex',
    },
  },

  /**
   * Hide the dotted value cue whenever the pointer is anywhere on the inline control OR when focus
   * is inside it (including icon/prefix affordance, clear/suffix buttons, or `:focus-visible` /
   * `:focus` on the `<input>`). Clearing from the `<input>` alone misses hover on siblings.
   */
  [`&:hover ${InlineInputField}, &:has(:focus-visible) ${InlineInputField}, &:has(${InlineInputField}:focus) ${InlineInputField}`]:
    {
      [controlTextDecoration]: 'none',
    },

  variants: {
    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
      false: {},
    },
    size: {
      'x-small': {
        ...controlVars.size['x-small'],
        ...SmallSansSizes,
      },
      small: {
        ...controlVars.size.small,
        ...SmallSansSizes,
      },
      medium: {
        ...controlVars.size.medium,
        ...BodySansSizes,
      },
      large: {
        ...controlVars.size.large,
        ...BodySansSizes,
      },
      'x-large': {
        ...controlVars.size['x-large'],
        ...LargeSansSizes,
      },
    },
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
    invalid: {
      true: {
        ...getVariantStyles('negative'),
      },
      false: {},
    },
  },
});
InlineInputContainer.displayName = 'InlineInputContainer';

type RemappedAriaTextFieldProps = RenameKeys<AriaTextFieldProps, { isDisabled: 'disabled' }>;

export interface InlineInputProps
  extends InputSharedProps,
    Omit<RemappedAriaTextFieldProps, 'placeholder'> {
  /**
   * Always provide copy for the empty state so the inline dotted underline is visible
   * (`::placeholder`); without it the field can look like plain body text.
   */
  placeholder: string;
  /**
   * Whether to show a "clear" button to delete the input content. Only available for controlled
   * inputs (those with `value` and `onChange` props).
   */
  hasClear?: boolean;
  /**
   * Whether to mark the input's value as invalid.
   */
  invalid?: boolean;
  /**
   * The size of the input.
   */
  size?: ControlSize;
  /**
   * Whether to trim whitespace from the input value on blur.
   */
  trimWhitespace?: boolean;
  /**
   * The variant of the input.
   */
  variant?: VariantProp;
  /**
   * The props to pass to the input element.
   */
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

export function InlineInput({
  disabled = false,
  hasClear = false,
  icon,
  invalid,
  maxWidth,
  minWidth,
  prefix,
  size = 'medium',
  suffix,
  trimWhitespace = true,
  type = 'text',
  variant = 'neutral',
  width,
  inputProps,
  ref,
  ...props
}: InlineInputProps & { ref?: React.Ref<HTMLDivElement | null> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const controlSize = useControlSize(size);
  const effectiveSubcontrolSize = getControlSubcontrolSize(controlSize);
  const { inputProps: ariaInputProps } = useTextField({ ...props, isDisabled: disabled }, inputRef);

  const finalInputProps: InputHTMLAttributes<HTMLInputElement> = {
    ...ariaInputProps,
    ...inputProps,
    type,
  };

  const { onChange } = props;

  const value = props?.value;

  if (trimWhitespace && onChange && typeof value === 'string') {
    const propsOnBlur = finalInputProps.onBlur;
    finalInputProps.onBlur = (e) => {
      if (propsOnBlur) {
        propsOnBlur(e);
      }
      onChange(value.trim());
    };
  }

  const hasValue = useMemo(() => !!value, [value]);
  const handleClear = useCallback(() => {
    onChange?.('');
    inputRef?.current?.focus();
  }, [onChange]);

  const hasPrefix = icon || prefix;

  const showClear = hasClear && !!onChange && hasValue;
  const hasSuffix = showClear || suffix;

  return (
    <InlineInputContainer
      ref={ref}
      size={controlSize}
      variant={variant}
      disabled={disabled}
      invalid={invalid ?? false}
      style={{ width, maxWidth, minWidth }}
    >
      <InlineInputElements>
        {hasPrefix && (
          <ControlProvider value={{ size: effectiveSubcontrolSize }}>
            <InlineInputAffix>
              {icon && <InlineInputIcon icon={icon} />}
              <InlineInputAffixContent>{prefix}</InlineInputAffixContent>
            </InlineInputAffix>
          </ControlProvider>
        )}
        <InlineInputField {...finalInputProps} ref={inputRef} />
        {hasSuffix && (
          <ControlProvider value={{ size: effectiveSubcontrolSize }}>
            <InlineInputAffix>
              {showClear ? (
                <InlineInputClear
                  onClick={handleClear}
                  appearance="muted"
                  arrangement="hidden-label"
                  ghost
                  icon="backspace"
                  variant="secondary"
                >
                  Clear
                </InlineInputClear>
              ) : undefined}
              <InlineInputAffixContent>{suffix}</InlineInputAffixContent>
            </InlineInputAffix>
          </ControlProvider>
        )}
      </InlineInputElements>
      <InlineInputBoundary />
    </InlineInputContainer>
  );
}
